import React, { useCallback, useEffect, useState } from 'react';
import { SectionGuide } from '../../components/pas/SectionGuide';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowLeftRight, ExternalLink } from 'lucide-react';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { SwapForm, SwapStatus } from '../../components/pas/SwapForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-swap`;

export default function PASSwap() {
  const { token } = usePASClientAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reqs, setReqs] = useState([]);
  const [bal, setBal] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/requests`, h).then(r => setReqs(r.data.requests)).catch(() => {});
    axios.get(`${API}/balances`, { ...h, params: { market: 'base' } }).then(r => setBal(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { if (!token) navigate('/pas/login?next=/pas/swap'); else load(); }, [token, navigate, load]);

  const getQuote = async (b) => {
    setBusy(true); setQuote(null);
    try { setQuote((await axios.post(`${API}/quote`, b, auth)).data); } catch (e) { toast.error(e.response?.data?.detail || 'No quote'); }
    setBusy(false);
  };
  const submit = async (b) => {
    setBusy(true);
    try { const r = await axios.post(`${API}/requests`, b, auth); toast.success(r.data.message); setQuote(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Request failed'); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-8" data-testid="swap-page">
      <div className="max-w-xl mx-auto space-y-6">
        <button onClick={() => navigate('/pas/dashboard')} className="text-slate-400 text-sm flex items-center gap-1" data-testid="swap-back-btn"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><ArrowLeftRight className="w-8 h-8 text-cyan-400" /> Exchange &amp; Swap</h1>
        </div>
        <SectionGuide id="swap" />
        {bal && <div className="grid grid-cols-3 gap-3 text-center" data-testid="swap-balances">
          {['ETH', 'USDC', 'BETH'].map(t => <div key={t} className="bg-slate-900 rounded-xl p-3 border border-slate-800"><p className="text-xs text-slate-400">{t === 'BETH' ? 'BETH USD' : t} on Base</p><p className="font-bold" data-testid={`swap-balance-${t}`}>{bal[t] == null ? '-' : Number(bal[t]).toLocaleString(undefined, { maximumFractionDigits: 6 })}</p></div>)}
        </div>}
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
          <SwapForm prefix="client" onQuote={getQuote} onSubmit={submit} submitLabel="Request swap" busy={busy} quote={quote} />
        </div>
        <div data-testid="swap-requests">
          <p className="text-sm text-slate-300 font-medium mb-2">My swap requests</p>
          {!reqs.length ? <p className="text-xs text-slate-500">None yet.</p> : reqs.map(r => (
            <div key={r.id} className="flex items-center justify-between py-2 border-b border-slate-800 text-sm" data-testid={`swap-request-${r.id}`}>
              <span>{r.amount} {r.token_in} → {r.token_out} · {r.market}</span>
              <span className="flex items-center gap-2"><SwapStatus s={r.status} />
                {r.result?.explorer_url && <a href={r.result.explorer_url} target="_blank" rel="noreferrer" className="text-emerald-400"><ExternalLink className="w-3 h-3" /></a>}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
